import React, { FC, useCallback, useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import { Stack } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { FormProvider, useForm } from 'react-hook-form';

import { sortListAlphabetical } from '../../../Draft/Component/Helper';
import ControlledTextField from '../../../RiverusUI/Components/ControlledTextField';
import CustomModal from '../../../RiverusUI/Components/CustomModal';
import RISelectComponent from '../../../RiverusUI/Components/SelectComponent';
import { addDictionaryContractType } from '../../../Services/dictionary';
import { getContractTypes } from '../../../Services/Template';

interface IProps {
  open: boolean;
  onClose: VoidFunction;
  setRefreshKey: any;
  setFilters: any;
  setSorting: any;
  initialFilter: any;
  setDataTableKey: any;
}

const CreateContractForm: FC<IProps> = (props) => {
  const {
    open,
    onClose,
    setRefreshKey,
    setFilters,
    setSorting,
    initialFilter,
    setDataTableKey,
  } = props;
  const methods = useForm();
  const { handleSubmit, control, watch, reset, setValue } = methods;

  const queryClient = useQueryClient();

  const contract = watch('contract');

  const { data: contracts, isLoading: contractLoading } = useQuery({
    queryKey: ['contract-list'],
    queryFn: getContractTypes,
    select: (response: any) => response?.results,
  });

  const filteredContracts = useMemo(() => {
    const uniqueNames = new Set();
    return contracts?.filter((contract: any) => {
      if (contract?.name && !uniqueNames.has(contract.name)) {
        uniqueNames.add(contract.name);
        return true;
      }
      return false;
    });
  }, [contracts]);

  const [tempValue, setTempValue] = useState(filteredContracts || []);
  const [idCounter, setIdCounter] = useState(filteredContracts?.length || 0);
  const [newContract, setNewContract] = useState<any>(null);

  const handleCloseModal = () => {
    setTempValue([]);
    setNewContract(null);
    onClose();
    reset();
  };

  const { mutate: createContractMutation, isPending: loadingCategory } =
    useMutation({
      mutationKey: ['create-contract'],
      mutationFn: addDictionaryContractType,
      onSuccess: () => {
        enqueueSnackbar('Contract type added successfully!', {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        queryClient.invalidateQueries({
          queryKey: ['contract-list'],
        });
        handleCloseModal();
        setDataTableKey(Math.random());
        setFilters(initialFilter);
        setSorting([]);
        setRefreshKey((prevKey: any) => !prevKey);
      },
      onError: (error: any) => {
        const responseData = error?.response?.data?.error?.[0];
        enqueueSnackbar(`${responseData || 'Failed to create contract type'}`, {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      },
    });

  const addNewValue = (value: string) => {
    const newId = idCounter + 1;
    const newValue = { name: value, value, id: newId };
    const updatedOptions = [newValue, ...filteredContracts];
    const isDuplicate = [...filteredContracts, ...tempValue]?.some(
      (contract: any) => contract?.name.toLowerCase() === value.toLowerCase()
    );
    if (isDuplicate) {
      enqueueSnackbar('Contract type already exists!', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return;
    }
    setTempValue(updatedOptions);
    setIdCounter(newId);
    setNewContract(newValue);
    setValue('contract', newValue.name);
  };

  const selectedContract = useMemo(() => {
    if (contract) {
      const selected = filteredContracts?.filter(
        (data: any) => data?.id === contract
      );
      return selected[0];
    }
    return null;
  }, [contract, filteredContracts]);

  const onSubmit = useCallback(
    (data: any) => {
      const payload = {
        name: selectedContract?.name || newContract?.value,
        displayName: data?.displayName,
        creation_type: selectedContract?.creation_type || 'custom',
      };
      createContractMutation(payload);
    },
    [createContractMutation, newContract, selectedContract]
  );

  return (
    <CustomModal
      maxWidth="md"
      title="Add Contract Type"
      open={open}
      handleClose={handleCloseModal}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack sx={{ minWidth: '400px', padding: '30px 10px' }} gap={2}>
            <RISelectComponent
              required
              name="contract"
              labelKey="name"
              options={sortListAlphabetical(
                tempValue?.length ? tempValue : filteredContracts,
                'name'
              )}
              control={control}
              loading={contractLoading}
              label="Select Contract Type"
              canCreateNew
              addNewValue={addNewValue}
            />
            <ControlledTextField
              required
              name="displayName"
              control={control}
              label="Display Name"
            />
            <LoadingButton
              variant="contained"
              sx={{ whiteSpace: 'nowrap' }}
              type="submit"
              loading={loadingCategory}
            >
              Submit
            </LoadingButton>
          </Stack>
        </form>
      </FormProvider>
    </CustomModal>
  );
};

export default CreateContractForm;
